import { memo } from 'react'
import { ChevronRight, FileText, PlayCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { getCompactEvalRunStatusLabel } from '../evalRunPresentation'
import type { EvalCaseListItem, EvalRunLifecycleStatus } from '../types'

interface EvalCaseListProps {
  cases: EvalCaseListItem[]
  queuedCaseIds: Set<string>
  runStatusByCaseId?: Map<string, EvalRunLifecycleStatus>
  onOpenCase: (caseId: string) => void
  onRunCase: (caseId: string) => void
}

export const EvalCaseList = memo(function EvalCaseList({
  cases,
  queuedCaseIds,
  runStatusByCaseId,
  onOpenCase,
  onRunCase,
}: EvalCaseListProps) {
  return (
    <div className="flex w-full flex-col gap-3">
      {cases.map((evalCase) => {
        const isQueued = queuedCaseIds.has(evalCase.id)
        const runStatus = runStatusByCaseId?.get(evalCase.id)
        const statusLabel = runStatus
          ? getCompactEvalRunStatusLabel(runStatus)
          : isQueued
            ? 'Pending'
            : null

        return (
          <article
            key={evalCase.id}
            className="relative flex min-h-20 w-full items-center gap-4 rounded-2xl border border-gray-200 bg-white px-5 py-3 shadow-sm transition-[border-color,box-shadow] hover:border-gray-300 hover:shadow-md">
            <button
              type="button"
              aria-label={`View ${evalCase.name}`}
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-4 text-left after:absolute after:inset-0 after:rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => onOpenCase(evalCase.id)}>
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary-ui-bg text-primary-solid">
                <FileText className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-base leading-6 font-semibold text-primary-high-contrast">
                  {evalCase.name}
                </h3>
              </div>
            </button>
            <div className="relative z-10 flex shrink-0 items-center gap-3">
              {statusLabel ? (
                <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-low-contrast">
                  {statusLabel}
                </span>
              ) : null}
              <Button
                type="button"
                variant={isQueued ? 'secondary' : 'outline'}
                size="icon"
                className="rounded-lg border-gray-200 text-primary-solid"
                aria-label={`Run ${evalCase.name} in ${evalCase.channel}`}
                title={isQueued ? 'Queued' : 'Run test'}
                onClick={() => onRunCase(evalCase.id)}>
                <PlayCircle className="h-5 w-5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="text-neutral-low-contrast"
                aria-label={`View details for ${evalCase.name}`}
                onClick={() => onOpenCase(evalCase.id)}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </article>
        )
      })}
    </div>
  )
})
